// Enums
// API interactions
import { apiSendOrderData } from '@src/api/order.api'
import { ChainId } from '@src/configs/chainInfo'
import { ResultCode, StepNames } from '@src/enums'
// Custom hooks
import { useWaitForTransaction } from '@src/hooks/useWaitForTransaction'
// Types
import { ChainHandler, IExchangeData, TronSwapParams, UpdateData } from '@src/types/exchange'
import { addressToBytes32 } from '@src/utils/addressBytes32'
import { getAllowance } from '@src/utils/getAllowance'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
// Utility functions
import { createContextLogger } from '@src/utils/logger'
import { processTronReceipt } from '@src/utils/processReceipt'
import { useTronTokenApproval, writeContractFunction } from '@src/utils/tron/utils'
// Add Zustand store
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useHistoryStore } from '@src/zustand_stores/history.store'

const logger = createContextLogger('useExchangeTokenTron')

export function useExchangeTokenTron(swapContract: any): ChainHandler {
    // Replace Redux selector with Zustand
    const referralCode = useApplicationStore(state => state.generatedReferralCode)
    const { setOrderId, setWaitingForConnectedWalletInteraction } = useApplicationStore()
    // Remove dispatch
    // const dispatch = useAppDispatch()
    const { waitForTransactionReceipt } = useWaitForTransaction()
    const { waitForApproval, approveToken } = useTronTokenApproval()
    const updateHistory = useHistoryStore(state => state.updateHistory)

    const handleApproval = async (updateData: UpdateData, sellData: IExchangeData, walletAddress: string) => {
        const allowance = await getAllowance(
            sellData.chainId,
            sellData.token?.address,
            walletAddress,
            swapContract.address
        )

        if (allowance >= sellData.value) {
            return
        }

        updateHistory(null, {
            ...updateData,
            currentStep: StepNames.APPROVE_TOKENS,
            result: { ...updateData.result, message: 'Please approve tokens.' },
        })

        const maxUint256 = BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff')

        const hash = await approveToken(sellData.token?.address, walletAddress, swapContract.address, maxUint256)

        updateHistory(null, {
            ...updateData,
            currentStep: StepNames.APPROVE_TOKENS_PENDING,
            result: {
                ...updateData.result,
                message: 'Waiting for approval confirmation.',
            },
        })

        await waitForApproval(hash)
    }

    const cleanAddress = (address: string) => {
        return address.substring(0, 42) // Take only the first 42 characters (0x + 40 hex chars)
    }

    // Organize the parameters into the expected structure
    const formatParamsForContract = (params: any, chainId?: number) => {
        // First tuple (address,bytes32,uint32) - direction info
        const direction = [
            cleanAddress(params.order_direction.src_token_address),
            chainId
                ? addressToBytes32(params.order_direction.dst_token_address, chainId)
                : params.order_direction.dst_token_address,
            params.order_direction.dst_lzc,
        ]

        // Second tuple (uint96,uint96,uint16,address,uint96) - funding info
        const funding = [
            params.order_funding.src_amount_in,
            params.order_funding.dst_amount_out,
            params.order_funding.bond_fee,
            cleanAddress(params.order_funding.bond_token_address),
            params.order_funding.bond_amount,
        ]

        // Third tuple (uint32,uint16,uint16) - expiration info
        const expiration = [
            params.order_expiration.timestamp,
            params.order_expiration.challenge_offset,
            params.order_expiration.challenge_window,
        ]

        // Fourth parameter (bytes32) - target address
        const targetAddress = chainId ? addressToBytes32(params.target_address, chainId) : params.target_address

        // Fifth parameter (address) - filler address
        const fillerAddress = cleanAddress(params.filler_address)

        return [direction, funding, expiration, targetAddress, fillerAddress]
    }

    const executeSwap = async (params: TronSwapParams, referrer?: string) => {
        const { sellData, quote, exchangeId, onSuccess } = params

        try {
            logger.info('Executing Tron swap', { params })

            const formattedParams = formatParamsForContract(quote?.order_data, params?.buyData?.chainId)

            const hash = await writeContractFunction(swapContract.address, 'placeOrder', formattedParams || [])

            if (!hash) {
                throw new Error('Could not send transaction.')
            }

            updateHistory(exchangeId, {
                currentStep: StepNames.CONFIRM_SWAP_PENDING,
                result: {
                    status: ResultCode.pending,
                    message: 'Waiting for confirmation.',
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            const receipt = await waitForTransactionReceipt(hash, false, true)

            updateHistory(exchangeId, {
                currentStep: StepNames.MATCHING,
                result: {
                    status: ResultCode.pending,
                    message: 'Order matching in progress.',
                    transactionETA: 60,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            const decodedData = processTronReceipt(receipt)

            const result = await apiSendOrderData(decodedData[0], referralCode.code, referrer)

            if (result?.status === ResultCode.failure) {
                throw new Error('Order failed to process')
            }

            updateHistory(exchangeId, {
                currentStep: StepNames.MATCH_CREATED,
                result: {
                    status: ResultCode.pending,
                    message: 'Order accepted. Order matching in-progress.',
                    transactionETA: 60,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            // Update Redux dispatch calls to Zustand
            setOrderId(result?.id?.toString() || '')

            // small delay to start timer, without instant Match Executed
            await new Promise(r => setTimeout(r, 250))

            updateHistory(exchangeId, {
                currentStep: StepNames.MATCH_EXECUTED,
                result: {
                    status: ResultCode.success,
                    message: 'Order successfully sent.',
                    transactionETA: result?.eta || 10,
                    sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                },
            })

            onSuccess()
        } catch (error) {
            logger.error('Tron swap failed:', error)
            throw error
        } finally {
            setWaitingForConnectedWalletInteraction(false)
        }
    }

    return {
        executeSwap,
        handleApproval,
    }
}
